import React, { useState, useEffect } from 'react';
import { refundService } from '../services/refundService';
import { 
  Search, 
  Filter, 
  CheckCircle, 
  XCircle, 
  Eye, 
  DollarSign, 
  Calendar,
  User,
  Mail,
  FileText,
  AlertCircle,
  Download,
  RefreshCw,
  MoreVertical,
  TrendingUp,
  Clock,
  Shield,
  BarChart3,
  CreditCard,
  AlertTriangle,
  ThumbsUp,
  ThumbsDown,
  Zap,
  Loader
} from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';

// Confirmation Modal Component
const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message, confirmText, confirmType = 'primary' }) => {
  if (!isOpen) return null;

  const typeConfigs = {
    primary: { bg: 'bg-blue-600 hover:bg-blue-700', text: 'text-white' },
    danger: { bg: 'bg-red-600 hover:bg-red-700', text: 'text-white' },
    success: { bg: 'bg-emerald-600 hover:bg-emerald-700', text: 'text-white' }
  };

  const config = typeConfigs[confirmType] || typeConfigs.primary;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl transform transition-all duration-300">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-t-2xl">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <AlertTriangle className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{title}</h2>
              <p className="text-sm text-gray-600 mt-1">Please confirm this action</p>
            </div>
          </div>
        </div>

        {/* Message */}
        <div className="p-6">
          <p className="text-gray-700 text-center">{message}</p>
        </div>

        {/* Actions */}
        <div className="flex space-x-3 px-6 py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`flex-1 px-4 py-3 text-sm font-semibold ${config.text} ${config.bg} rounded-xl transition-all duration-200 transform hover:-translate-y-0.5 shadow-lg`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

// Refund Details Modal
const RefundDetailsModal = ({ refund, isOpen, onClose }) => {
  if (!isOpen) return null;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const safeFormatAmount = (amount) => {
    if (typeof amount === 'number') return amount.toFixed(2);
    if (typeof amount === 'string') {
      const parsed = parseFloat(amount);
      return isNaN(parsed) ? '0.00' : parsed.toFixed(2);
    }
    return '0.00';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Refund Details</h2>
                <p className="text-sm text-gray-600">Complete information for refund #{refund.id}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors duration-200"
            >
              <XCircle className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Student Information</h3>
                <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                  <div className="flex items-center space-x-3">
                    <User className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-600">Name</p>
                      <p className="font-medium text-gray-900">{refund.student_name || 'Unknown Student'}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-600">Email</p>
                      <p className="font-medium text-gray-900">{refund.student_email || 'No email'}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Assignment Details</h3>
                <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                  <div className="flex items-center space-x-3">
                    <FileText className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-600">Title</p>
                      <p className="font-medium text-gray-900">{refund.assignment_title || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <DollarSign className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-600">Amount</p>
                      <p className="font-medium text-gray-900">
                        ${safeFormatAmount(refund.assignment_amount || refund.refund_amount || refund.amount)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Timeline</h3>
                <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <div>
                      <p className="text-xs text-gray-600">Request Submitted</p>
                      <p className="font-medium text-gray-900">{formatDate(refund.created_at)}</p>
                    </div>
                  </div>
                  {refund.processed_at && (
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                      <div>
                        <p className="text-xs text-gray-600">Processed</p>
                        <p className="font-medium text-gray-900">{formatDate(refund.processed_at)}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Status</h3>
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    refund.status === 'pending' ? 'bg-amber-100 text-amber-800' :
                    refund.status === 'approved' ? 'bg-emerald-100 text-emerald-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {refund.status === 'pending' && <Clock className="h-3 w-3 mr-1" />}
                    {refund.status === 'approved' && <CheckCircle className="h-3 w-3 mr-1" />}
                    {refund.status === 'rejected' && <XCircle className="h-3 w-3 mr-1" />}
                    {refund.status?.charAt(0).toUpperCase() + refund.status?.slice(1)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Reason & Notes */}
          <div className="grid grid-cols-1 gap-6">
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Refund Reason</h3>
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-gray-700">{refund.reason || 'No reason provided'}</p>
              </div>
            </div>

            {refund.admin_notes && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Admin Notes</h3>
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                  <p className="text-blue-700">{refund.admin_notes}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const RefundCard = ({ refund, onProcess, loading }) => {
  const [showActions, setShowActions] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  const safeFormatAmount = (amount) => {
    if (typeof amount === 'number') return amount.toFixed(2);
    if (typeof amount === 'string') {
      const parsed = parseFloat(amount);
      return isNaN(parsed) ? '0.00' : parsed.toFixed(2);
    }
    return '0.00';
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: {
        color: 'bg-amber-100 text-amber-800 border-amber-200',
        icon: Clock,
        label: 'Pending Review',
        badgeColor: 'from-amber-500 to-amber-600'
      },
      approved: {
        color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
        icon: CheckCircle,
        label: 'Approved',
        badgeColor: 'from-emerald-500 to-emerald-600'
      },
      rejected: {
        color: 'bg-red-100 text-red-800 border-red-200',
        icon: XCircle,
        label: 'Rejected',
        badgeColor: 'from-red-500 to-red-600'
      },
      processing: {
        color: 'bg-blue-100 text-blue-800 border-blue-200',
        icon: RefreshCw,
        label: 'Processing',
        badgeColor: 'from-blue-500 to-blue-600'
      }
    };
    return configs[status] || configs.pending;
  };

  const statusConfig = getStatusConfig(refund.status);
  const StatusIcon = statusConfig.icon;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleActionClick = (action) => {
    setPendingAction(action);
    setShowConfirmModal(true);
    setShowActions(false);
  };

  const handleConfirmAction = () => {
    if (pendingAction) {
      onProcess(refund.id, pendingAction);
    }
    setShowConfirmModal(false);
    setPendingAction(null);
  };

  const getActionConfig = (action) => {
    const configs = {
      approve: {
        title: 'Approve Refund',
        message: `Are you sure you want to approve this refund request for $${safeFormatAmount(refund.assignment_amount || refund.refund_amount || refund.amount)}?`,
        confirmText: 'Yes, Approve',
        type: 'success'
      },
      reject: {
        title: 'Reject Refund',
        message: `Are you sure you want to reject this refund request? This action cannot be undone.`,
        confirmText: 'Yes, Reject',
        type: 'danger'
      }
    };
    return configs[action] || configs.approve;
  };

  const actionConfig = pendingAction ? getActionConfig(pendingAction) : null;

  return (
    <>
      <div className="bg-white rounded-2xl border border-gray-200 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
        <div className="p-6">
          <div className="flex items-start justify-between">
            {/* Left Section - Refund Info */}
            <div className="flex-1">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className={`p-3 rounded-2xl bg-gradient-to-r ${statusConfig.badgeColor}`}>
                    <DollarSign className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      Refund #{refund.id || 'N/A'}
                    </h3>
                    <p className="text-gray-500 text-sm">Student: {refund.student_name || 'Unknown Student'}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold ${statusConfig.color} border`}>
                    <StatusIcon className="h-4 w-4 mr-2" />
                    {statusConfig.label}
                  </span>
                </div>
              </div>

              {/* Quick Info Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="space-y-3">
                  <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl">
                    <User className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-600">Student</p>
                      <p className="text-gray-900 font-semibold">{refund.student_name || 'Unknown Student'}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl">
                    <FileText className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-600">Assignment</p>
                      <p className="text-gray-900 font-semibold">{refund.assignment_title || 'N/A'}</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl">
                    <CreditCard className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-600">Amount</p>
                      <p className="text-2xl font-bold text-gray-900">
                        ${safeFormatAmount(refund.assignment_amount || refund.refund_amount || refund.amount)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl">
                    <Calendar className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-600">Requested</p>
                      <p className="text-gray-900 font-semibold">{formatDate(refund.created_at)}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              {refund.status === 'pending' && (
                <div className="flex items-center space-x-3 mb-4">
                  <button
                    onClick={() => handleActionClick('approve')}
                    disabled={loading}
                    className="flex items-center space-x-2 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 disabled:opacity-50 transition-colors duration-200 font-medium"
                  >
                    <ThumbsUp className="h-4 w-4" />
                    <span>Approve Refund</span>
                  </button>
                  <button
                    onClick={() => handleActionClick('reject')}
                    disabled={loading}
                    className="flex items-center space-x-2 px-4 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 disabled:opacity-50 transition-colors duration-200 font-medium"
                  >
                    <ThumbsDown className="h-4 w-4" />
                    <span>Reject Refund</span>
                  </button>
                  <button
                    onClick={() => setShowDetails(true)}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors duration-200 font-medium"
                  >
                    <Eye className="h-4 w-4" />
                    <span>View Details</span>
                  </button>
                </div>
              )}
            </div>

            {/* Action Menu */}
            <div className="relative ml-4">
              <button
                onClick={() => setShowActions(!showActions)}
                className="p-3 text-gray-400 hover:text-gray-600 rounded-xl hover:bg-gray-50 transition-colors duration-200 border border-gray-200"
              >
                <MoreVertical className="h-5 w-5" />
              </button>

              {showActions && (
                <div className="absolute right-0 top-12 w-64 bg-white rounded-2xl border border-gray-200 shadow-2xl z-20 py-3">
                  <button
                    onClick={() => {
                      setShowDetails(true);
                      setShowActions(false);
                    }}
                    className="flex items-center space-x-3 w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                  >
                    <Eye className="h-4 w-4" />
                    <span>View Full Details</span>
                  </button>
                  
                  {refund.status === 'pending' && (
                    <>
                      <button
                        onClick={() => handleActionClick('approve')}
                        disabled={loading}
                        className="flex items-center space-x-3 w-full px-4 py-3 text-sm text-emerald-600 hover:bg-emerald-50 disabled:opacity-50 transition-colors duration-200"
                      >
                        <ThumbsUp className="h-4 w-4" />
                        <span>Approve Refund</span>
                      </button>
                      <button
                        onClick={() => handleActionClick('reject')}
                        disabled={loading}
                        className="flex items-center space-x-3 w-full px-4 py-3 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors duration-200"
                      >
                        <ThumbsDown className="h-4 w-4" />
                        <span>Reject Refund</span>
                      </button>
                    </>
                  )}
                  
                  <div className="border-t border-gray-200 mt-2 pt-2">
                    <button className="flex items-center space-x-3 w-full px-4 py-3 text-sm text-blue-600 hover:bg-blue-50 transition-colors duration-200">
                      <Download className="h-4 w-4" />
                      <span>Export Details</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {actionConfig && (
        <ConfirmationModal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={handleConfirmAction}
          title={actionConfig.title}
          message={actionConfig.message}
          confirmText={actionConfig.confirmText}
          confirmType={actionConfig.type}
        />
      )}

      {/* Details Modal */}
      <RefundDetailsModal
        refund={refund}
        isOpen={showDetails}
        onClose={() => setShowDetails(false)}
      />
    </>
  );
};

const StatCard = ({ title, value, icon: Icon, color = 'blue', trend, subtitle, description }) => {
  const colorConfigs = {
    blue: { 
      bg: 'bg-blue-50', 
      iconBg: 'bg-blue-100',
      icon: 'text-blue-600', 
      text: 'text-blue-700', 
      border: 'border-blue-200',
      gradient: 'from-blue-500 to-blue-600'
    },
    emerald: { 
      bg: 'bg-emerald-50',
      iconBg: 'bg-emerald-100',
      icon: 'text-emerald-600', 
      text: 'text-emerald-700', 
      border: 'border-emerald-200',
      gradient: 'from-emerald-500 to-emerald-600'
    },
    amber: { 
      bg: 'bg-amber-50',
      iconBg: 'bg-amber-100',
      icon: 'text-amber-600', 
      text: 'text-amber-700', 
      border: 'border-amber-200',
      gradient: 'from-amber-500 to-amber-600'
    },
    red: { 
      bg: 'bg-red-50',
      iconBg: 'bg-red-100',
      icon: 'text-red-600', 
      text: 'text-red-700', 
      border: 'border-red-200',
      gradient: 'from-red-500 to-red-600'
    }
  };

  const config = colorConfigs[color] || colorConfigs.blue;

  return (
    <div className={`${config.bg} ${config.border} rounded-2xl border p-6 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1`}>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-semibold text-gray-600 mb-2">{title}</p>
          <p className={`text-3xl font-bold ${config.text} mb-2`}>{value}</p>
          {subtitle && (
            <p className="text-sm text-gray-500 mb-1">{subtitle}</p>
          )}
          {description && (
            <p className="text-xs text-gray-400">{description}</p>
          )}
          {trend && (
            <div className={`flex items-center space-x-1 mt-3 text-sm font-medium ${
              trend.direction === 'up' ? 'text-emerald-600' : 'text-red-600'
            }`}>
              <TrendingUp className={`h-4 w-4 ${trend.direction === 'down' ? 'rotate-180' : ''}`} />
              <span>{trend.value}</span>
            </div>
          )}
        </div>
        <div className={`p-4 rounded-2xl ${config.iconBg}`}>
          <Icon className={`h-6 w-6 ${config.icon}`} />
        </div>
      </div>
    </div>
  );
};

const FilterPill = ({ status, count, isActive, onClick }) => {
  const statusConfigs = {
    pending: { 
      color: 'bg-amber-500', 
      hover: 'hover:bg-amber-600', 
      active: 'bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-lg',
      icon: Clock
    },
    approved: { 
      color: 'bg-emerald-500', 
      hover: 'hover:bg-emerald-600', 
      active: 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-lg',
      icon: CheckCircle
    },
    rejected: { 
      color: 'bg-red-500', 
      hover: 'hover:bg-red-600', 
      active: 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg',
      icon: XCircle
    },
    all: { 
      color: 'bg-blue-500', 
      hover: 'hover:bg-blue-600', 
      active: 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg',
      icon: BarChart3
    }
  };

  const config = statusConfigs[status] || statusConfigs.all;
  const StatusIcon = config.icon;

  return (
    <button
      onClick={onClick}
      className={`flex items-center space-x-3 px-6 py-3 rounded-xl font-semibold transition-all duration-200 transform hover:-translate-y-0.5 ${
        isActive 
          ? `${config.active} text-white shadow-lg` 
          : `bg-gray-100 text-gray-700 ${config.hover} hover:text-white hover:shadow-md`
      }`}
    >
      <StatusIcon className="h-4 w-4" />
      <span className="capitalize">{status}</span>
      {count > 0 && (
        <span className={`px-2 py-1 rounded-full text-xs font-bold ${
          isActive ? 'bg-white text-gray-700' : `${config.color} text-white`
        }`}>
          {count}
        </span>
      )}
    </button>
  );
};

const RefundManagement = () => {
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    totalAmount: 0
  });

  const safeFormatAmount = (amount) => {
    if (typeof amount === 'number') return amount.toFixed(2);
    if (typeof amount === 'string') {
      const parsed = parseFloat(amount);
      return isNaN(parsed) ? '0.00' : parsed.toFixed(2);
    }
    return '0.00';
  };

  useEffect(() => {
    fetchRefunds();
  }, [statusFilter]);

  const fetchRefunds = async () => {
    try {
      setLoading(true);
      
      const response = await refundService.getRefundRequests({
        status: statusFilter === 'all' ? null : statusFilter,
        page: 1,
        limit: 50
      });
      
      let refundsData = [];
      
      if (Array.isArray(response.data?.data?.refunds)) {
        refundsData = response.data.data.refunds;
      } else if (Array.isArray(response.data?.refunds)) {
        refundsData = response.data.refunds;
      } else if (Array.isArray(response.data?.data)) {
        refundsData = response.data.data;
      } else if (Array.isArray(response.data)) {
        refundsData = response.data;
      }
      
      setRefunds(refundsData);
      
      // Calculate comprehensive statistics
      const totalAmount = refundsData.reduce((sum, refund) => {
        const amount = refund.assignment_amount || refund.refund_amount || refund.amount || 0;
        return sum + parseFloat(amount);
      }, 0);

      const pendingAmount = refundsData
        .filter(r => r.status === 'pending')
        .reduce((sum, refund) => sum + parseFloat(refund.assignment_amount || refund.refund_amount || refund.amount || 0), 0);

      const approvedAmount = refundsData
        .filter(r => r.status === 'approved')
        .reduce((sum, refund) => sum + parseFloat(refund.assignment_amount || refund.refund_amount || refund.amount || 0), 0);
      
      setStats({
        total: refundsData.length,
        pending: refundsData.filter(r => r.status === 'pending').length,
        approved: refundsData.filter(r => r.status === 'approved').length,
        rejected: refundsData.filter(r => r.status === 'rejected').length,
        totalAmount,
        pendingAmount,
        approvedAmount
      });
      
    } catch (error) {
      console.error('Error fetching refunds:', error);
      setRefunds([]);
    } finally {
      setLoading(false);
    }
  };

  const handleProcessRefund = async (refundId, action) => {
    try {
      setProcessing(prev => ({ ...prev, [refundId]: true }));
      
      await refundService.processRefund(refundId, {
        action,
        adminNotes: `Refund ${action}d by admin`
      });
      
      fetchRefunds();
    } catch (error) {
      console.error('Error processing refund:', error);
      alert(`Error ${action}ing refund: ${error.response?.data?.message || 'Please try again'}`);
    } finally {
      setProcessing(prev => ({ ...prev, [refundId]: false }));
    }
  };

  const filteredRefunds = Array.isArray(refunds) ? refunds.filter(refund => {
    if (!searchTerm) return true;
    
    const studentName = refund.student_name || refund.student?.name || refund.student?.email || 'Unknown Student';
    const refundId = refund.id?.toString() || '';
    
    return studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
           refundId.includes(searchTerm) ||
           (refund.reason && refund.reason.toLowerCase().includes(searchTerm.toLowerCase()));
  }) : [];

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="flex items-center space-x-4 mb-2">
                <div className="p-3 bg-white rounded-2xl shadow-lg">
                  <CreditCard className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-gray-900">Refund Management</h1>
                  <p className="text-gray-600 mt-2 text-lg">Manage and process student refund requests efficiently</p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3 mt-6 lg:mt-0">
              <button
                onClick={fetchRefunds}
                className="flex items-center space-x-2 px-6 py-3 bg-white border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-all duration-200 font-semibold shadow-sm hover:shadow-md"
              >
                <RefreshCw className="h-5 w-5" />
                <span>Refresh Data</span>
              </button>
              <button className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
                <Download className="h-5 w-5" />
                <span>Export Report</span>
              </button>
            </div>
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              title="Total Refunds"
              value={stats.total}
              icon={BarChart3}
              color="blue"
              description="All refund requests"
            />
            <StatCard
              title="Pending Review"
              value={stats.pending}
              icon={Clock}
              color="amber"
              subtitle={`$${safeFormatAmount(stats.pendingAmount)} pending`}
              description="Requires immediate attention"
            />
            <StatCard
              title="Approved"
              value={stats.approved}
              icon={CheckCircle}
              color="emerald"
              subtitle={`$${safeFormatAmount(stats.approvedAmount)} approved`}
              description="Successfully processed"
            />
            <StatCard
              title="Total Amount"
              value={`$${safeFormatAmount(stats.totalAmount)}`}
              icon={DollarSign}
              color="red"
              description="Across all refund requests"
            />
          </div>

          {/* Filters */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-lg">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              {/* Status Filters */}
              <div className="flex-1">
                <label className="block text-sm font-semibold text-gray-700 mb-3">Filter by Status</label>
                <div className="flex flex-wrap gap-3">
                  {['all', 'pending', 'approved', 'rejected'].map((status) => (
                    <FilterPill
                      key={status}
                      status={status}
                      count={status === 'all' ? stats.total : stats[status]}
                      isActive={statusFilter === status}
                      onClick={() => setStatusFilter(status)}
                    />
                  ))}
                </div>
              </div>

              {/* Search */}
              <div className="lg:w-80">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Search Refunds</label>
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-12 w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                    placeholder="Search by student, ID, or reason..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Results Header */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-gray-900">
                {statusFilter === 'all' ? 'All' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)} Refunds
              </h3>
              <p className="text-sm text-gray-600">
                Showing {filteredRefunds.length} of {refunds.length} refund requests
              </p>
            </div>
            <div className="text-sm text-gray-500 flex items-center space-x-2">
              <Zap className="h-4 w-4 text-amber-500" />
              <span>Updated just now</span>
            </div>
          </div>

          {/* Refunds List */}
          <div className="space-y-6">
            {loading ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-16 text-center shadow-lg">
                <Loader className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-6" />
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Loading Refund Requests</h3>
                <p className="text-gray-600 text-lg">Fetching refund requests from the server...</p>
              </div>
            ) : filteredRefunds.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-16 text-center shadow-lg">
                <CreditCard className="h-20 w-20 text-gray-400 mx-auto mb-6" />
                <h3 className="text-2xl font-bold text-gray-900 mb-3">No Refund Requests Found</h3>
                <p className="text-gray-600 text-lg mb-8">
                  {searchTerm || statusFilter !== 'all'
                    ? 'No refund requests match your current filters. Try adjusting your search criteria.'
                    : `There are no ${statusFilter} refund requests at the moment.`}
                </p>
                {(searchTerm || statusFilter !== 'all') && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setStatusFilter('all');
                    }}
                    className="px-8 py-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all duration-200 font-semibold text-lg shadow-lg hover:shadow-xl"
                  >
                    Clear All Filters
                  </button>
                )}
              </div>
            ) : (
              filteredRefunds.map((refund) => (
                <RefundCard
                  key={refund.id}
                  refund={refund}
                  onProcess={handleProcessRefund}
                  loading={processing[refund.id]}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default RefundManagement;